<template>
    <v-dialog
        :value="isOpen"
        scrollable
        max-width="900px"
        transition="slide-y-transition"
        eager
        persistent
        open-delay="1000"
        @keydown.esc="onClose"
        @click:outside="onClose"
    >
        <SimpleUniversalAgreeDialog
            :is-open="simpleUniversalAgreeDialog"
            title="Nieprawidłowe dane"
            description="Format komponentu jest większy niż format arkusza"
            @on-agree="closeSimpleUniversalAgreeDialog"
        />

        <v-card v-if="hasData">
            <v-card-title>
                <h1 class="text-subtitle-1">
                    Parametry procesu: <strong>{{ taskData.process.name }}</strong> - ilość arkuszy: <strong>{{ taskData.bruttoAmount }}</strong>, kompletów płyt: <strong>{{ impositionData.numberOfPlateSet }}</strong>
                </h1>
            </v-card-title>


            <v-card-text v-if="imposition.impositionTask.length > 0" class="pa-0">
                <v-container fluid class="px-6 py-2">
                    <v-row>
                        <v-col cols="12" sm="8">
                            <v-autocomplete
                                v-model="task.machine"
                                label="Maszyna"
                                :items="filterMachinesByTask(task, machines)"
                                item-text="name"
                                return-object
                                outlined
                                dense
                                hide-details
                                :disabled="!isJobEditable"
                                @change="machineChanged"
                            />
                        </v-col>
                    </v-row>
                    <v-row>
                        <v-col cols="12" sm="4" class="pb-0">
                            <v-autocomplete
                                v-model="imposition.paper"
                                label="Rodzaj papieru"
                                :items="papers"
                                item-text="name"
                                return-object
                                outlined
                                dense
                                hide-details
                                :disabled="!isJobEditable"
                                @change="paperChanged"
                            />
                        </v-col>
                        <v-col cols="12" md="6" class="pb-0">
                            <v-text-field
                                v-model="imposition.paperDescription"
                                label="Nazwa papieru"
                                dense
                                outlined
                                counter="256"
                                :error="paperDescriptionErrors"
                                :disabled="!isJobEditable"
                            />
                        </v-col>
                    </v-row>
                    <v-row>
                        <v-col cols="12" md="4" class="pt-0">
                            <v-checkbox
                                v-model="certifiedPaper"
                                label="Certyfikat"
                                class="mb-1 mt-0"
                                :readonly="false"
                                :disabled="isJobClosed"
                                hide-details
                                @change="handleCertifiedPaperChange"
                            />
                        </v-col>
                        <v-col cols="12" md="4" class="pt-0">
                            <v-checkbox
                                v-model="imposition.sendPaperWaste"
                                label="Wysyłka makulatury"
                                class="mb-1 mt-0"
                                disabled
                                hide-details
                            />
                        </v-col>
                        <v-col cols="12" md="4" class="pt-0">
                            <v-checkbox
                                v-model="imposition.patternSheets"
                                label="Arkusze sygnalne"
                                class="mb-1 mt-0"
                                hide-details
                            />
                        </v-col>
                    </v-row>
                    <v-row>
                        <v-col cols="12" md="9" sm="9">
                            <v-autocomplete
                                ref="certificateSelect"
                                v-model="imposition.paperCertificateId"
                                :items="certificates"
                                label="Nazwa certyfikatu"
                                item-value="id"
                                item-text="name"
                                no-data-text="Brak danych..."
                                hide-details
                                outlined
                                dense
                                :disabled="isJobClosed || !certifiedPaper"
                                :error="paperCertificateErrors"
                                @change="handleCertificateChange"
                            />
                        </v-col>
                    </v-row>
                    <v-row>
                        <v-col cols="12" sm="4">
                            <v-text-field
                                v-mask="'#####'"
                                :value="imposition.formatA"
                                label="Format A"
                                suffix="mm"
                                dense
                                outlined
                                :hint="impositionFormatHint(imposition)"
                                persistent-hint
                                :hide-details="!isJobEditable"
                                :error="impositionFormatAErrors"
                                :readonly="!isJobEditable"
                                @input="handleFormatA"
                                @change="changeFormatA"
                            />
                        </v-col>
                        <v-col cols="12" sm="4">
                            <v-text-field
                                v-mask="'#####'"
                                :value="imposition.formatB"
                                label="Format B"
                                suffix="mm"
                                dense
                                outlined
                                :hint="impositionFormatHint(imposition)"
                                persistent-hint
                                :hide-details="!isJobEditable"
                                :error="impositionFormatBErrors"
                                :readonly="!isJobEditable"
                                @input="handleFormatB"
                                @change="changeFormatB"
                            />
                        </v-col>
                        <v-col cols="12" sm="4">
                            <v-text-field
                                v-mask="'######'"
                                :value="imposition.extraWasteAmount"
                                label="Dodatkowe straty na zepsucie"
                                suffix="szt."
                                dense
                                outlined
                                hide-details
                                :disabled="!isJobEditable"
                                @change="changeExtraWasteAmount"
                            />
                        </v-col>
                    </v-row>
                    <v-row>
                        <v-col cols="12" sm="4">
                            <v-text-field
                                :value="parseColorsValue(imposition.impositionColoursA)"
                                readonly
                                label="Kolorystyka A"
                                dense
                                outlined
                                append-icon="mdi-cog"
                                :disabled="!isJobEditable"
                                hide-details
                                :error="impositionColoursAErrors"
                                @click="showColorSettingsDialog('A', task.machine.id)"
                                @click:append="showColorSettingsDialog('A', task.machine.id)"
                            />
                        </v-col>
                        <v-col cols="12" sm="4">
                            <v-text-field
                                :value="parseColorsValue(imposition.impositionColoursB)"
                                readonly
                                label="Kolorystyka B"
                                dense
                                outlined
                                append-icon="mdi-cog"
                                :disabled="!isJobEditable || isOneSidedPrintType()"
                                :placeholder="imposition.printTypeId === 6 ? 'bez zadruku' : ''"
                                hide-details
                                :error="impositionColoursBErrors"
                                @click="showColorSettingsDialog('B', task.machine.id)"
                                @click:append="showColorSettingsDialog('B', task.machine.id)"
                            />
                        </v-col>
                        <v-col cols="12" md="4">
                            <v-select
                                v-model="imposition.serviceId"
                                label="Usługa"
                                :items="task.machine ? task.machine.serviceRead : []"
                                item-text="name"
                                item-value="id"
                                dense
                                outlined
                                hide-details
                                :disabled="!isJobEditable"
                            />
                        </v-col>
                        <v-col cols="12" sm="4">
                            <v-select
                                :value="imposition.printTypeId"
                                label="Sposób druku"
                                :items="task.machine ? task.machine.printTypeRead : []"
                                item-text="name"
                                item-value="id"
                                outlined
                                dense
                                :error="printTypeIdErrors"
                                hide-details
                                :disabled="!isJobEditable"
                                @change="changePrintType"
                            />
                        </v-col>
                        <v-col cols="12" sm="4">
                            <v-select
                                v-model="imposition.plateTypeId"
                                label="Rodzaj płyty"
                                :items="task.machine ? hasOnePlateType(task.machine.plateTypeRead) : []"
                                item-text="name"
                                item-value="id"
                                outlined
                                :error="plateTypeIdErrors"
                                hide-details
                                dense
                                :disabled="!isJobEditable || plateDoesNotApply"
                            />
                        </v-col>
                    </v-row>

                    <v-row>
                        <v-col cols="12" sm="4">
                            <v-text-field
                                v-mask="'##'"
                                :value="imposition.sideA"
                                label="Stron A"
                                outlined
                                dense
                                hide-details
                                :disabled="!isJobEditable"
                                @change="changeSideA"
                            />
                        </v-col>
                        <v-col cols="12" sm="4">
                            <v-text-field
                                v-mask="'##'"
                                :value="sideB"
                                label="Stron B"
                                outlined
                                dense
                                hide-details
                                :placeholder="imposition.printTypeId === 6 ? 'bez zadruku' : ''"
                                :disabled="!isJobEditable || isOneSidedPrintType()"
                                @change="changeSideB"
                            />
                        </v-col>
                        <v-col cols="12" sm="4">
                            <v-text-field
                                v-mask="'######'"
                                :value="imposition.sheetCountStep"
                                label="Liczenie arkuszy co"
                                outlined
                                dense
                                hide-details
                                :disabled="!isJobEditable"
                                @change="changeSheetCountStep"
                            />
                        </v-col>

                        <v-col cols="12" sm="4">
                            <v-select
                                v-model="imposition.colourTemplate"
                                label="Wzór kolorystyczny"
                                :items="jobColourTemplates"
                                item-text="name"
                                return-object
                                outlined
                                dense
                                hide-details
                                :disabled="!isJobEditable"
                                @change="colorTemplateChanged"
                            />
                        </v-col>
                        <v-col cols="12" md="4">
                            <v-select
                                :value="parseElasticMaterialValue(task.impositionTaskMaterial)"
                                :items="filteredElasticMaterials"
                                label="Guma"
                                item-value="id"
                                item-text="name"
                                dense
                                outlined
                                return-object
                                hide-details
                                :disabled="!isJobEditable || isElasticFieldDisabled"
                                @click:clear="clearElasticMaterial(task.impositionTaskMaterial)"
                                @change="saveElasticMaterial(task.impositionTaskMaterial, $event)"
                            />
                        </v-col>
                        <v-col
                            v-for="property in task.impositionTaskProperties"
                            :key="property.id"
                            cols="12"
                            md="4"
                        >
                            <v-autocomplete
                                v-if="property.productionProcessProperty.productionProcessPropertyValues.length > 0"
                                v-model="property.value"
                                :items="property.productionProcessProperty.productionProcessPropertyValues"
                                :label="property.productionProcessProperty.name | capitalize"
                                item-value="name"
                                item-text="name"
                                dense
                                outlined
                                hide-details
                                :disabled="!isJobEditable || isPropertyDisabled(property)"
                                clearable
                            />
                            <v-text-field
                                v-else
                                v-model="property.value"
                                :label="property.productionProcessProperty.name | capitalize"
                                dense
                                outlined
                                :disabled="!isJobEditable || isPropertyDisabled(property)"
                                hide-details
                            />
                        </v-col>
                    </v-row>

                    <v-row>
                        <v-col cols="12" sm="6">
                            <v-text-field
                                v-model="task.wasteAmount"
                                label="Straty"
                                suffix="szt."
                                outlined
                                dense
                                :error-messages="taskWasteAmountErrors"
                                required
                                :disabled="!isJobEditable"
                            />
                        </v-col>
                        <v-col cols="12" sm="6">
                            <v-text-field
                                v-model="task.wastePercent"
                                label="Straty"
                                suffix="%"
                                outlined
                                dense
                                :error-messages="taskWastePercentErrors"
                                required
                                :disabled="!isJobEditable"
                            />
                        </v-col>
                    </v-row>

                    <v-row>
                        <v-col>
                            <v-textarea
                                :value="task.description"
                                label="Uwagi"
                                auto-grow
                                rows="1"
                                dense
                                outlined
                                clearable
                                hide-details
                                :disabled="!isJobEditable"
                                @input="parseDescriptionValue"
                            />
                        </v-col>
                    </v-row>
                </v-container>

                <v-divider />

                <v-container fluid class="px-6 py-2">
                    <v-row>
                        <v-col cols="12">
                            <h1 class="text-subtitle-1">
                                Komponenty
                            </h1>
                            <v-data-table
                                :headers="headers"
                                :items="taskData.impositionTaskOrderComponents"
                                :mobile-breakpoint="0"
                                hide-default-footer
                                disable-pagination
                            >
                                <template #item.productionNumber="{item}">
                                    {{ item.orderProductComponent.productionNumber }}
                                </template>
                                <template #item.productName="{item}">
                                    {{ item.orderProductComponent.productName }}
                                </template>
                                <template #item.componentName="{item}">
                                    {{ item.orderProductComponent.name }}
                                </template>
                                <template #item.componentFormat="{item}">
                                    <v-edit-dialog
                                        v-if="isJobEditable && isPrintTask"
                                        large
                                        lazy
                                        cancel-text="Anuluj"
                                        save-text="Zapisz"
                                        @open="openOrderProductComponentFormat(item.orderProductComponent)"
                                        @save="saveOrderProductComponentFormat(item.orderProductComponent)"
                                        @cancel="cancelOrderProductComponentFormat(item.orderProductComponent)"
                                    >
                                        <div class="d-inline-flex">
                                            {{ item.orderProductComponent.formatA }}x{{ item.orderProductComponent.formatB }}
                                            <v-icon color="primary" small class="ml-1">
                                                mdi-pencil
                                            </v-icon>
                                        </div>
                                        <template #input>
                                            <v-text-field
                                                v-model.number="item.orderProductComponent.formatA"
                                                v-mask="'#####'"
                                                label="Format A"
                                                single-line
                                                dense
                                                :error="formatAErrors(item)"
                                            />
                                            <v-text-field
                                                v-model.number="item.orderProductComponent.formatB"
                                                v-mask="'#####'"
                                                label="Format B"
                                                required
                                                single-line
                                                dense
                                                :error="formatBErrors(item)"
                                            />
                                        </template>
                                    </v-edit-dialog>
                                    <template v-else>
                                        {{ item.orderProductComponent.formatA }}x{{ item.orderProductComponent.formatB }}
                                    </template>
                                </template>
                                <template #item.parts="{item}">
                                    {{ item.parts }}
                                </template>
                                <template #item.nettoAmount="{item}">
                                    {{ item.nettoAmount }}
                                </template>
                            </v-data-table>
                        </v-col>
                    </v-row>
                </v-container>
            </v-card-text>

            <v-divider />

            <v-card-actions>
                <template v-if="isJobEditable">
                    <v-btn
                        color="error"
                        text
                        data-sentry-label="Anuluj"
                        @click="onClose"
                    >
                        Anuluj
                    </v-btn>
                    <v-spacer />
                    <v-btn
                        color="success"
                        text
                        data-sentry-label="Zapisz"
                        @click="saveImpositionAndTask"
                    >
                        Zapisz
                    </v-btn>
                </template>
                <template v-else>
                    <v-btn
                        color="error"
                        text
                        data-sentry-label="Zamknij"
                        @click="onClose"
                    >
                        Zamknij
                    </v-btn>
                </template>
            </v-card-actions>
        </v-card>
    </v-dialog>
</template>

<script>
import { appConfig } from '@/config'

import { errorHandler } from '@/utils/errorHandler.js'
import { fourDigitsAfterDecimal } from '@/utils/customValidators'

import parse from '@/mixins/parse'
import formats from './mixins/formats'

import { required, requiredIf, integer, between, maxLength, helpers } from 'vuelidate/lib/validators'
import { mask } from 'vue-the-mask'
import SimpleUniversalAgreeDialog from '@/components/common/SimpleUniversalAgreeDialog'

const ELASTIC_NUMBER_FIELD_ID = 5 // ID dla wlaściwości numer gumy

export default {
    name: 'ImpositionParametersDialog',
    directives: { mask },
    components: {
        SimpleUniversalAgreeDialog
    },
    mixins: [ formats, parse ],
    props: {
        isOpen: {
            type: Boolean
        },
        jobId: {
            type: Number,
            default: null
        },
        jobStatusId: {
            type: Number,
            default: null
        },
        impositionData: {
            type: Object,
            default: null
        },
        taskData: {
            type: Object,
            default: null
        },
        papers: {
            type: Array,
            default: null
        },
        jobColourTemplates: {
            type: Array,
            default: null
        },
        machines: {
            type: Array,
            default: () => []
        },
        isJobEditable: {
            type: Boolean
        },
        elasticMaterials: {
            type: Array,
            default: () => []
        },
    },
    data() {
        return {
            simpleUniversalAgreeDialog: false,
            imposition: {
                formatA: null,
                formatB: null,
                impositionColoursA: null,
                impositionColoursB: null,
                colourTemplate: null,
                sideA: null,
                sideB: null,
                paper: {
                    name: null
                },
                paperId: null,
                paperDescription: null,
                printTypeId: null,
                plateTypeId: null,
                varnishing: null,
                foliation: null,
                impositionTask: [],
                paperCertificateId: null,
            },
            currentOrderProductComponent: null,
            sideB: null,
            task: {},
            headers: [
                {
                    text: 'Nr zamówienia',
                    value: 'productionNumber',
                    sortable: false
                },
                {
                    text: 'Nazwa produktu',
                    value: 'productName',
                    sortable: false
                },
                {
                    text: 'Komponent',
                    value: 'componentName',
                    sortable: false,
                    align: 'center'
                },
                {
                    text: 'Format',
                    value: 'componentFormat',
                    sortable: false,
                    align: 'center'
                },
                {
                    text: 'Użytki',
                    value: 'parts',
                    sortable: false,
                    align: 'center'
                },
                {
                    text: 'Nakład netto',
                    value: 'nettoAmount',
                    sortable: false,
                    align: 'center'
                }
            ],
            isCertifiedPaper: this.impositionData?.paperCertificateId ? true : false,
        }
    },
    validations() {
        const impositionFormatRangeHelper = () => {
            return helpers.withParams({ type: 'impositionFormatRangeHelper' }, (value, impositionModel) => {
                if (impositionModel.impositionTask.length > 0) {
                    const { formatARangeX, formatARangeY, formatBRangeX, formatBRangeY } = this.formatRangeValidations

                    return (formatARangeX && formatBRangeY) || (formatARangeY && formatBRangeX)
                } else {
                    return true
                }
            })
        }
        const formatRangeByComponentSizeHelper = () => {
            return helpers.withParams({ type: 'formatRangeByComponentSizeHelper' }, () => {
                if (this.task.impositionTaskOrderComponents.length == 1) {
                    const { formatA, formatB } = this.formatRangeByComponentSizeValidations

                    return (formatA && formatB)
                }

                return true
            })
        }

        const impositionColoursBHelper = () => {
            return helpers.withParams({ type: 'impositionColoursBHelper' }, (value, impositionModel) => {
                if (impositionModel.impositionTask.length > 0) {
                    const isValid = value.length > 0

                    return impositionModel.printTypeId !== 6 ? isValid : true
                } else {
                    return true
                }
            })
        }

        const rules = {
            imposition: {
                printTypeId: {
                    required
                },
                paperDescription: {
                    maxLength: maxLength(256)
                },
                plateTypeId: {
                    required
                },
                paperCertificateId: {
                    required: requiredIf(function() {
                        return this.certifiedPaper && !this.isJobClosed
                    }) 
                },
                formatA: {
                    isValid: (formatRangeByComponentSizeHelper() && impositionFormatRangeHelper())
                },
                formatB: {
                    isValid: (formatRangeByComponentSizeHelper() && impositionFormatRangeHelper())
                },
                impositionColoursA: {
                    required
                },
                impositionColoursB: {
                    required: impositionColoursBHelper()
                },
            },
            task: {
                wasteAmount: {
                    required,
                    integer
                },
                wastePercent: {
                    required,
                    fourDigitsAfterDecimal,
                    between: between(0, appConfig.jobs.maxWastePercent)
                },
                impositionTaskOrderComponents: {
                    $each: {
                        orderProductComponent: {
                            formatA: {
                                required,
                                integer
                            },
                            formatB: {
                                required,
                                integer
                            }
                        }
                    }
                }
            }
        }

        return rules
    },
    computed: {
        certifiedPaper: {
            get() {
                return this.isCertifiedPaper
            },
            set(value) {
                if (!value) {
                    this.imposition.paperCertificateId = null
                }
                this.isCertifiedPaper = value

                return value
            }
        },
        hasData() {
            return Object.keys(this.imposition).length !== 0 && Object.keys(this.task).length !== 0
        },
        plateDoesNotApply() {
            return this.imposition.plateTypeId === 7
        },
        certificates() {
            return this.$store.state.configurations.papers.certificates
        },
        isJobClosed() {
            return this.jobStatusId === 40 // zamknięte
        },

        //Walidacje
        paperDescriptionErrors() {
            return this.$v.imposition.paperDescription.$invalid
        },
        impositionFormatAErrors() {
            return this.$v.imposition.formatA.$invalid
        },
        impositionFormatBErrors() {
            return this.$v.imposition.formatB.$invalid
        },
        impositionColoursAErrors() {
            return this.$v.imposition.impositionColoursA.$invalid
        },
        impositionColoursBErrors() {
            return this.$v.imposition.impositionColoursB.$invalid
        },
        printTypeIdErrors() {
            return this.$v.imposition.printTypeId.$invalid
        },
        plateTypeIdErrors() {
            return this.$v.imposition.plateTypeId.$invalid
        },
        paperCertificateErrors() {
            return this.$v.imposition.paperCertificateId.$invalid
        },
        hasElasticMaterial() {
            return this.task.impositionTaskMaterial.find(material => material.materialTypeId === 24)
        },
        taskWasteAmountErrors() {
            const errors = []

            if (!this.$v.task.wasteAmount.required || !this.$v.task.wasteAmount.integer) {
                errors.push('Proszę podać prawidłową wartość')
            }

            return errors
        },
        taskWastePercentErrors() {
            const errors = []

            if (!this.$v.task.wastePercent.required || !this.$v.task.wastePercent.between || !this.$v.task.wastePercent.fourDigitsAfterDecimal) {
                errors.push('Proszę podać prawidłową wartość z zakresu 0-25')
            }

            return errors
        },
        canMoveForward() {
            if (this.task.impositionTaskOrderComponents.length == 1) {
                const { formatA, formatB } = this.formatRangeByComponentSizeValidations

                return (formatA && formatB)
            }

            return true
        },
        formatRangeByComponentSizeValidations() {
            let minWidth = +this.task.impositionTaskOrderComponents[0].outputFormatA
            let minHeight = +this.task.impositionTaskOrderComponents[0].outputFormatB
            
            if (
                (this.imposition.formatA > this.imposition.formatB && minHeight > minWidth) ||
                (this.imposition.formatB > this.imposition.formatA && minWidth > minHeight)
            ) {
                [minHeight, minWidth] = [minWidth, minHeight]
            }

            return {
                formatA: this.imposition.formatA >= minWidth,
                formatB: this.imposition.formatB >= minHeight
            }
        },
        isPrintTask() {
            return this.task.processId === appConfig.common.processes.printTaskId
        },
        getCuttingRuberMaterial() {
            return this.task.machine.serviceRead.find(item =>
                item.name === 'druk + lakier wybiórczo (wycinanie gumy)'
            ) || {
                id: null,
                name: null,
            }
        },
        getArchivedRubberMaterial() {
            return this.task.machine.serviceRead.find(item =>
                item.name === 'druk + lakier wybiórczo (zarchiwizowana guma)'
            ) || {
                id: null,
                name: null,
            }
        },
        isElasticFieldDisabled() {
            return [
                this.getCuttingRuberMaterial,
                this.getArchivedRubberMaterial,
            ].find(item => item.id === this.imposition.serviceId) === undefined
        },
        filteredElasticMaterials() {
            if (this.imposition.serviceId == this.getCuttingRuberMaterial.id) {
                return this.elasticMaterials.filter(item => item.name.includes('Nowa guma'))

            } if (this.imposition.serviceId == this.getArchivedRubberMaterial.id) {
                return this.elasticMaterials.filter(item => item.name.includes('Stara guma'))
            }

            return this.elasticMaterials
        },
    },
    watch: {
        impositionData: {
            handler: function(newValue) {
                this.imposition = JSON.parse(JSON.stringify(newValue))
                this.isCertifiedPaper = !!newValue.paperCertificateId
            },
            deep: true
        },
        taskData: {
            handler: function(newValue) {
                this.task = JSON.parse(JSON.stringify(newValue))
            },
            deep: true
        },
    },
    methods: {
        formatAErrors(item) {
            const index = this.task.impositionTaskOrderComponents.findIndex(
                component => component.orderProductComponent.id === item.orderProductComponent.id
            )

            if (index !== -1) {
                return this.$v.task.impositionTaskOrderComponents.$each[index].orderProductComponent.formatA.$invalid
            }

            return false
        },
        formatBErrors(item) {
            const index = this.task.impositionTaskOrderComponents.findIndex(
                component => component.orderProductComponent.id === item.orderProductComponent.id
            )

            if (index !== -1) {
                return this.$v.task.impositionTaskOrderComponents.$each[index].orderProductComponent.formatB.$invalid
            }

            return false
        },
        openOrderProductComponentFormat(orderProductComponent) {
            this.currentOrderProductComponent = JSON.parse(JSON.stringify(orderProductComponent))
        },
        saveOrderProductComponentFormat(orderProductComponent) {
            const index = this.task.impositionTaskOrderComponents.findIndex(
                component => component.orderProductComponent.id === orderProductComponent.id
            )

            if (this.$v.task.impositionTaskOrderComponents.$each[index].orderProductComponent.$invalid) {
                this.$store.dispatch('showNotification', {
                    type: 'error',
                    message: 'Proszę podać prawidłowy format.'
                })

                orderProductComponent.formatA = this.currentOrderProductComponent.formatA
                orderProductComponent.formatB = this.currentOrderProductComponent.formatB
                this.$v.$reset()

                return
            }

            return this.$store.dispatch('orders/updateOrderProductComponent', {
                id: orderProductComponent.id,
                formatA: orderProductComponent.formatA,
                formatB: orderProductComponent.formatB
            })
                .then(response => {
                    this.task.impositionTaskOrderComponents[index].orderProductComponent = JSON.parse(JSON.stringify(response.data))
                    this.$v.$reset()
                })
                .catch(error => {
                    errorHandler(error)
                })
        },
        cancelOrderProductComponentFormat(orderProductComponent) {
            orderProductComponent.formatA = this.currentOrderProductComponent.formatA
            orderProductComponent.formatB = this.currentOrderProductComponent.formatB
            this.$v.$reset()
        },
        hasOnePlateType(plateTypes) {
            if (plateTypes.length === 1) {
                this.imposition.plateTypeId = plateTypes[0].id
            }

            return plateTypes
        },
        isOneSidedPrintType() {
            if (this.imposition.printTypeId === 6) {
                this.imposition.sideB = 0
                this.sideB = null

                return true
            }
            this.sideB = this.imposition.sideB

            return false
        },
        saveElasticMaterial(impositionTaskMaterial, elasticMaterial) {
            if (typeof elasticMaterial !== 'undefined') {
                const index = impositionTaskMaterial.findIndex(material => material.materialTypeId === 24)
                const selectedElasticMaterial = {
                    materialId: elasticMaterial.id,
                    materialTypeId: elasticMaterial.materialTypeId,
                    value: 1
                }

                if (index > -1) {
                    impositionTaskMaterial.splice(index, 1, selectedElasticMaterial)
                } else {
                    impositionTaskMaterial.push(selectedElasticMaterial)
                }
            }
        },
        clearElasticMaterial(impositionTaskMaterial) {
            const index = impositionTaskMaterial.findIndex(material => material.materialTypeId === 24)

            impositionTaskMaterial.splice(index, 1)
        },
        parseElasticMaterialValue(impositionTaskMaterial) {
            const material = impositionTaskMaterial.find(material => material.materialTypeId === 24)

            return material?.materialId
        },
        getPrintColours(printMachineId) {
            this.$store.dispatch('jobs/getJobPrintColors', {
                query: `?machine_id=${printMachineId}`,
            })
        },
        showColorSettingsDialog(side, machineId) {
            this.getPrintColours(machineId)
            this.$emit('show-color-settings-dialog', {
                imposition: this.imposition,
                side: side
            })
        },
        closeColorSettingsDialog() {
            this.$emit('close-color-settings-dialog')
        },
        paperChanged(paper) {
            this.imposition.paperId = paper.id
        },
        colorTemplateChanged(colorTemplate) {
            this.imposition.colourTemplateId = colorTemplate.id
        },
        machineChanged(machine) {
            this.imposition.impositionColoursA = []
            this.imposition.impositionColoursB = []
            this.task.machineId = machine.id
            this.imposition.printTypeId = null
            this.imposition.plateTypeId = null
            this.imposition.serviceId = null
        },
        changePrintType(value) {
            this.imposition.printTypeId = value

            if (this.isOneSidedPrintType) {
                this.imposition.impositionColoursB = []
            }
        },
        changeExtraWasteAmount(value) {
            this.imposition.extraWasteAmount = value || 0
        },
        changeSideA(value) {
            this.imposition.sideA = value || 0
        },
        changeSideB(value) {
            this.imposition.sideB = value || 0
        },
        changeSheetCountStep(value) {
            this.imposition.sheetCountStep = value || 0
        },
        saveImpositionAndTask() {
            if (!this.canMoveForward) {
                this.simpleUniversalAgreeDialog = true
                
                return
            }
            if (this.$v.$invalid && this.isJobClosed) {
                this.$store.dispatch('showNotification', {
                    type: 'error',
                    message: this.$i18n.t('validations.invalidMessage')
                })

                return
            }

            this.$v.$invalid

            this.$store.dispatch('toggleCircularLoader', true)

            this.$store.dispatch('jobs/saveImposition', this.imposition)
                .then(() => {
                    this.$store.dispatch('jobs/saveTask', this.task)
                        .then(() => {
                            this.$store.dispatch('jobs/getJob', this.jobId)
                                .then(response => {
                                    this.$emit('on-save', response.data)
                                    this.onClose()
                                })
                                .catch(error => {
                                    errorHandler(error)
                                })
                                .finally(() => {
                                    this.$store.dispatch('toggleCircularLoader', false)
                                })
                        })
                        .catch(error => {
                            errorHandler(error)
                            this.$store.dispatch('toggleCircularLoader', false)
                        })
                })
                .catch(error => {
                    this.$store.dispatch('toggleCircularLoader', false)
                    errorHandler(error)
                })
        },
        closeSimpleUniversalAgreeDialog() {
            this.simpleUniversalAgreeDialog = false
        },
        onClose() {
            setTimeout(() => {
                this.imposition = JSON.parse(JSON.stringify(this.impositionData))
                this.task = JSON.parse(JSON.stringify(this.taskData))
                this.$v.$reset()
            }, 500)

            this.$emit('on-close')
        },
        filterMachinesByTask(task, machines) {
            return machines.filter(item => item.productionProcess.includes(task.processId))
        },
        parseDescriptionValue(value) {
            this.task.description = value || null
        },
        handleCertificateChange(certificateId) {
            this.imposition.paperCertificateId = certificateId
        },
        handleCertifiedPaperChange(checked) {
            if (!checked) {
                this.imposition.paperCertificate = null
                this.imposition.paperCertificateId = null
                this.$refs.certificateSelect.internalValue = null
            } 
        },
        isPropertyDisabled(property) {
            return property.productionProcessProperty.id === ELASTIC_NUMBER_FIELD_ID &&
                this.getArchivedRubberMaterial.id !== this.imposition.serviceId
        },
    }
}
</script>
